<template>
  <div class="about">
    <!-- Hero Section -->
    <section class="ct-hero-section">
      <div class="hero-background">
        <div class="hero-image-container">
          <img src="@/assets/img/hero-ct.jpg" class="hero-background-image" alt="CT Grid Team">
          <div class="hero-gradient-overlay"></div>
        </div>
      </div>
      <div class="container ct-hero-content">
        <div class="row">
          <div class="col-lg-8 mx-auto text-center">
            <h1 class="display-4 fw-bold mb-4">{{ t('about.title') }}</h1>
            <p class="lead mb-4">{{ t('about.subtitle') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- About Content -->
    <section class="ct-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-10 mx-auto">
            <p class="lead mb-4" v-html="t('about.lead')"></p>

            <h2 class="ct-title mt-4">{{ t('about.platform.title') }}</h2>
            <p class="mb-4" v-html="t('about.platform.intro')"></p>

            <div class="feature-cards mb-4">
              <div class="ct-card p-3 mb-3">
                <h4><i class="fas fa-sync-alt text-ct-orange me-2"></i>{{ t('about.platform.ingestion.title') }}</h4>
                <p class="mb-0">{{ t('about.platform.ingestion.description') }}</p>
              </div>

              <div class="ct-card p-3 mb-3">
                <h4><i class="fas fa-chart-line text-ct-orange me-2"></i>{{ t('about.platform.calculations.title') }}</h4>
                <p class="mb-0">{{ t('about.platform.calculations.description') }}</p>
              </div>

              <div class="ct-card p-3 mb-3">
                <h4><i class="fas fa-share-alt text-ct-orange me-2"></i>{{ t('about.platform.distribution.title') }}</h4>
                <p class="mb-0">{{ t('about.platform.distribution.description') }}</p>
              </div>

              <div class="ct-card p-3 mb-3">
                <h4><i class="fas fa-rocket text-ct-orange me-2"></i>{{ t('about.platform.onboarding.title') }}</h4>
                <p class="mb-0">{{ t('about.platform.onboarding.description') }}</p>
              </div>
            </div>

            <h2 class="ct-title mt-4">{{ t('about.difference.title') }}</h2>

            <div class="row g-3 mb-4">
              <div class="col-md-6 mb-3">
                <div class="ct-card p-3 h-100">
                  <h4 class="mb-2"><i class="fas fa-balance-scale text-ct-orange me-2"></i>{{ t('about.difference.independent.title') }}</h4>
                  <p class="mb-0">{{ t('about.difference.independent.description') }}</p>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="ct-card p-3 h-100">
                  <h4 class="mb-2"><i class="fas fa-check-circle text-ct-orange me-2"></i>{{ t('about.difference.compliance.title') }}</h4>
                  <p class="mb-0">{{ t('about.difference.compliance.description') }}</p>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="ct-card p-3 h-100">
                  <h4 class="mb-2"><i class="fas fa-shield-alt text-ct-orange me-2"></i>{{ t('about.difference.security.title') }}</h4>
                  <p class="mb-0">{{ t('about.difference.security.description') }}</p>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="ct-card p-3 h-100">
                  <h4 class="mb-2"><i class="fas fa-globe text-ct-orange me-2"></i>{{ t('about.difference.global.title') }}</h4>
                  <p class="mb-0">{{ t('about.difference.global.description') }}</p>
                </div>
              </div>
            </div>

            <h2 class="ct-title">{{ t('about.commitment.title') }}</h2>
            <p class="mb-4">
              {{ t('about.commitment.description') }}
            </p>

            <div class="text-center mb-4 pb-2">
              <h3 class="mb-3">{{ t('about.callToAction') }}</h3>
              <div class="d-flex flex-wrap justify-content-center gap-3">
                <router-link to="/contact" class="ct-btn-primary">{{ t('about.buttons.demo') }}</router-link>
                <router-link to="/for-traders" class="ct-btn-outline">{{ t('about.buttons.solutions') }}</router-link>
                <router-link to="/contact" class="ct-btn-outline">{{ t('about.buttons.contact') }}</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';

export default {
  name: 'AboutView',
  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },
  created() {
    this.setPageTitle(this.t('about.title'));
  }
}
</script>

<style scoped>
.text-ct-orange {
  color: var(--ct-orange);
}

.ct-hero-section {
  position: relative;
  padding: 5rem 0 3rem;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background-color: var(--ct-dark);
}

.hero-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  overflow: hidden;
  background-color: var(--ct-dark);
}

.hero-background-image {
  position: absolute;
  opacity: 0.3;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
  transform: translate(-5%, 0%);
  left: 0;
  top: 0;
  pointer-events: none;
}

.hero-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(18, 20, 23, 0.7) 0%, rgba(18, 20, 23, 0.9) 70%);
  z-index: 1;
}

.ct-hero-content {
  position: relative;
  z-index: 2;
}

.ct-section {
  padding: 3rem 0 4rem;
}

.lead {
  font-size: 1.15rem;
  line-height: 1.7;
}

.ct-card {
  background-color: var(--ct-dark-tertiary);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.ct-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
}

.ct-title {
  color: var(--ct-orange);
  font-weight: 700;
  margin-bottom: 1rem;
  font-size: 1.8rem;
}

.ct-card h4 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.7rem;
}

.feature-cards .ct-card {
  margin-bottom: 0.75rem;
}

.ct-btn-primary,
.ct-btn-outline {
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  display: inline-block;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.ct-btn-primary {
  background-color: var(--ct-orange);
  color: #000;
  border: none;
}

.ct-btn-primary:hover {
  background-color: var(--ct-orange-hover);
  color: #000;
}

.ct-btn-outline {
  border: 1px solid var(--ct-orange);
  color: var(--ct-orange);
  background: transparent;
}

.ct-btn-outline:hover {
  background-color: var(--ct-orange);
  color: #000;
}

@media (max-width: 768px) {
  .ct-section {
    padding: 2rem 0;
  }

  .ct-hero-section {
    padding: 4rem 0 2rem;
  }

  .ct-title {
    font-size: 1.5rem;
  }

  .ct-card h4 {
    font-size: 1.1rem;
  }

  .lead {
    font-size: 1rem;
  }
}
</style>